import { Link } from "react-router-dom";
import {
  ArrowRight,
  CalendarCheck,
  CheckCircle2,
  CheckSquare,
  FileText,
  Flame,
  MonitorPlay,
  PenLine,
  RotateCcw,
  Sparkles,
  Square,
  Target,
  TrendingUp,
} from "lucide-react";
import Reveal from "./Reveal";

/* Alternating editorial showcases — realistic product-UI fragments, not cards. */

const Eyebrow = ({ children, testId }) => (
  <p data-testid={testId} className="mb-3">
    <span className="inline-flex items-center gap-2 rounded-lg border border-[#16A34A]/15 bg-[#16A34A]/8 px-3.5 py-1.5 text-[13px] font-bold uppercase tracking-[0.08em] text-[#14532D] sm:text-[14px] lg:text-[15px]">
      <span className="pulse-dot inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-[#16A34A]" />
      {children}
    </span>
  </p>
);

const Showcase = ({
  eyebrow,
  headline,
  copy,
  ctaHref,
  flip = false,
  sectionTestId,
  eyebrowTestId,
  headlineTestId,
  ctaTestId,
  children,
}) => (
  <section
    data-testid={sectionTestId}
    className="px-4 py-16 sm:px-8 md:py-24 lg:px-12"
  >
    <div className="mx-auto grid max-w-7xl items-center gap-10 lg:grid-cols-12 lg:gap-14">
      <Reveal className={`lg:col-span-7 ${flip ? "lg:order-2" : "lg:order-1"}`}>
        <div className="relative rounded-[1.75rem] border border-[#14532D]/10 bg-white p-5 shadow-[0_10px_30px_-10px_rgba(20,83,45,0.08)] sm:p-8">
          {children}
        </div>
      </Reveal>
      <Reveal
        delay={0.12}
        className={`lg:col-span-5 ${flip ? "lg:order-1" : "lg:order-2"}`}
      >
        <Eyebrow testId={eyebrowTestId}>{eyebrow}</Eyebrow>
        <h2
          data-testid={headlineTestId}
          className="font-display text-[32px] font-bold leading-[1.15] tracking-tight text-[#17201A] sm:text-[42px] lg:text-[50px]"
        >
          {headline}
        </h2>
        <p className="mt-4 max-w-md font-body text-[17px] leading-[1.6] text-[#647067] sm:text-[18px] lg:text-[19px]">
          {copy}
        </p>
        <Link
          to={ctaHref}
          data-testid={ctaTestId}
          className="group mt-6 inline-flex items-center gap-1.5 text-[16px] font-bold text-[#16A34A] transition-colors duration-200 hover:text-[#14532D] sm:text-[17px]"
        >
          Learn more
          <ArrowRight className="h-4.5 w-4.5 transition-transform duration-200 group-hover:translate-x-1" />
        </Link>
      </Reveal>
    </div>
  </section>
);

/* ---------- 2. Practice & Learn (visual LEFT) ---------- */

const PracticeLearnVisual = () => (
  <div className="relative">
    <div className="rounded-2xl border border-[#E2E8F0] bg-[#F8FAF7] p-5 sm:p-6">
      <div className="flex items-center justify-between">
        <p className="flex items-center gap-2 text-[10px] font-extrabold uppercase tracking-[0.14em] text-[#16A34A]">
          <PenLine className="h-3.5 w-3.5" />
          Custom Practice
        </p>
        <span className="rounded-full border border-[#FACC15]/50 bg-[#FEF9C3] px-2.5 py-1 text-[10px] font-bold text-[#14532D]">
          CBSE · 2025
        </span>
      </div>
      <p className="mt-3 font-display text-lg font-extrabold text-[#17201A]">
        Mathematics — Quadratic Equations
      </p>
      <p className="text-sm font-semibold text-[#647067]">
        5-question session · Medium difficulty
      </p>
      <div className="mt-4 space-y-2">
        {["Concept refresher", "Solve 5 questions", "Review solutions"].map(
          (s, i) => (
            <div
              key={s}
              className="flex items-center gap-2.5 rounded-xl border border-[#E2E8F0] bg-white px-3.5 py-2.5 text-sm font-semibold text-[#17201A]"
            >
              {i < 2 ? (
                <CheckSquare className="h-4 w-4 shrink-0 text-[#16A34A]" />
              ) : (
                <Square className="h-4 w-4 shrink-0 text-[#94A39B]" />
              )}
              {s}
            </div>
          )
        )}
      </div>
      <div className="mt-4 flex items-center justify-between">
        <span className="text-xs font-bold text-[#647067]">Progress</span>
        <span className="flex gap-1">
          {[0, 1, 2, 3, 4].map((i) => (
            <span
              key={i}
              className={`h-2 w-2 rounded-full ${i < 3 ? "bg-[#16A34A]" : "bg-[#E2E8F0]"}`}
            />
          ))}
        </span>
      </div>
      <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-[#E2E8F0]">
        <div className="h-full w-3/5 rounded-full bg-[#16A34A]" />
      </div>
    </div>
    {/* Overlapping fragments */}
    <div className="mt-4 flex flex-wrap gap-3 sm:absolute sm:-bottom-6 sm:right-6 sm:mt-0 sm:flex-col sm:gap-2.5">
      <div className="flex items-center gap-2 rounded-xl border border-[#E2E8F0] bg-white px-3.5 py-2.5 shadow-sm sm:-rotate-2">
        <Target className="h-4 w-4 text-[#16A34A]" />
        <span className="text-xs font-bold text-[#17201A]">
          Smart Practice targets weak areas
        </span>
      </div>
      <div className="flex items-center gap-2 rounded-xl border border-[#E2E8F0] bg-white px-3.5 py-2.5 shadow-sm sm:rotate-1">
        <MonitorPlay className="h-4 w-4 text-[#16A34A]" />
        <span className="text-xs font-bold text-[#17201A]">
          Watch &amp; Learn — full-chapter videos
        </span>
      </div>
      <div className="flex items-center gap-2 rounded-xl border border-[#E2E8F0] bg-white px-3.5 py-2.5 shadow-sm sm:-rotate-1">
        <FileText className="h-4 w-4 text-[#16A34A]" />
        <span className="text-xs font-bold text-[#17201A]">
          PYQs — previous year sets
        </span>
      </div>
    </div>
    <div className="hidden sm:block sm:h-8" />
  </div>
);

export const PracticeLearnSection = () => (
  <Showcase
    sectionTestId="practice-learn-showcase"
    eyebrowTestId="practice-learn-eyebrow"
    headlineTestId="practice-learn-headline"
    ctaTestId="practice-learn-cta"
    eyebrow="Practice & Learn"
    headline="Turn every study session into meaningful practice."
    copy="Learn a concept, practice it, evaluate your understanding, and keep improving — all in one preparation flow."
    ctaHref="/practice-setup"
  >
    <PracticeLearnVisual />
  </Showcase>
);

/* ---------- 3. Ask Abhyas AI (text LEFT / visual RIGHT) ---------- */

const AskAiVisual = () => (
  <div>
    <div className="flex items-center gap-2.5 border-b border-[#E2E8F0] pb-4">
      <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#16A34A]/10">
        <Sparkles className="h-4.5 w-4.5 text-[#16A34A]" />
      </span>
      <div>
        <p className="text-sm font-extrabold text-[#17201A]">Ask Abhyas</p>
        <p className="text-[11px] font-semibold text-[#647067]">
          Your AI study assistant · Class 10 Mathematics
        </p>
      </div>
    </div>
    <div className="mt-5 flex justify-end">
      <div className="max-w-[85%] rounded-2xl rounded-br-md bg-[#17201A] px-4 py-3 text-sm font-semibold leading-relaxed text-white">
        Can you explain how the discriminant determines the roots of a
        quadratic equation?
      </div>
    </div>
    <div className="mt-4 max-w-[92%] rounded-2xl rounded-bl-md border border-[#16A34A]/15 bg-[#F8FAF7] px-4 py-3.5">
      <p className="text-sm font-semibold leading-relaxed text-[#17201A]">
        For ax² + bx + c = 0, the discriminant is{" "}
        <span className="rounded bg-white px-1.5 py-0.5 font-display font-bold text-[#14532D]">
          D = b² − 4ac
        </span>
      </p>
      <ul className="mt-3 space-y-2 text-sm leading-relaxed text-[#647067]">
        <li className="flex gap-2">
          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[#16A34A]" />
          <span>
            <b className="text-[#17201A]">D &gt; 0</b> — two real, distinct
            roots
          </span>
        </li>
        <li className="flex gap-2">
          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[#16A34A]" />
          <span>
            <b className="text-[#17201A]">D = 0</b> — one repeated real root
          </span>
        </li>
        <li className="flex gap-2">
          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[#16A34A]" />
          <span>
            <b className="text-[#17201A]">D &lt; 0</b> — no real roots
          </span>
        </li>
      </ul>
      <div className="mt-3.5 rounded-xl border border-[#FACC15]/40 bg-[#FEF9C3] px-3 py-2.5">
        <p className="text-xs font-bold leading-relaxed text-[#14532D]">
          Board tip — “nature of roots” questions appear almost every year in
          CBSE Class 10.
        </p>
      </div>
    </div>
    <div className="mt-4 flex items-center gap-2 rounded-full border border-[#E2E8F0] bg-white px-4 py-3">
      <span className="flex-1 text-sm font-medium text-[#94A39B]">
        Ask a follow-up…
      </span>
      <ArrowRight className="h-4 w-4 text-[#16A34A]" />
    </div>
  </div>
);

export const AskAiSection = () => (
  <Showcase
    flip
    sectionTestId="ask-ai-showcase"
    eyebrowTestId="ask-ai-eyebrow"
    headlineTestId="ask-ai-headline"
    ctaTestId="ask-ai-cta"
    eyebrow="Ask Abhyas AI"
    headline="Your questions don't have to wait."
    copy="Ask questions, understand difficult concepts, and get guidance based on what you're actually preparing for."
    ctaHref="/abhyas-ai"
  >
    <AskAiVisual />
  </Showcase>
);

/* ---------- 4. Study Planner (visual LEFT) ---------- */

const StudyPlannerVisual = () => (
  <div>
    <div className="flex items-center justify-between rounded-2xl border border-[#16A34A]/20 bg-[#16A34A]/8 px-4 py-3.5">
      <div>
        <p className="text-[11px] font-extrabold uppercase tracking-[0.12em] text-[#14532D]">
          Upcoming exam
        </p>
        <p className="mt-0.5 font-display text-base font-extrabold text-[#17201A]">
          CBSE Class 12 Boards
        </p>
      </div>
      <div className="text-right">
        <p className="font-display text-2xl font-extrabold text-[#16A34A]">24</p>
        <p className="text-[10px] font-bold uppercase tracking-wide text-[#647067]">
          days left
        </p>
      </div>
    </div>
    <div className="mt-4 grid gap-4 sm:grid-cols-2">
      <div className="rounded-2xl border border-[#E2E8F0] bg-[#F8FAF7] p-4">
        <p className="text-[11px] font-extrabold uppercase tracking-[0.12em] text-[#647067]">
          Today's tasks · 3 of 4
        </p>
        <ul className="mt-2.5 space-y-2">
          {[
            { t: "Physics — Wave Optics practice", done: true },
            { t: "Maths — 5-question Integration set", done: true },
            { t: "Watch: Ray Optics (12 min)", done: true },
            { t: "Chemistry — revise Aldehydes", done: false },
          ].map((task) => (
            <li
              key={task.t}
              className="flex items-start gap-2 text-xs font-semibold leading-snug"
            >
              {task.done ? (
                <CheckSquare className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#16A34A]" />
              ) : (
                <Square className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#94A39B]" />
              )}
              <span className={task.done ? "text-[#647067]" : "text-[#17201A]"}>
                {task.t}
              </span>
            </li>
          ))}
        </ul>
      </div>
      <div className="space-y-4">
        <div className="rounded-2xl border border-[#E2E8F0] bg-white p-4">
          <p className="text-[11px] font-extrabold uppercase tracking-[0.12em] text-[#647067]">
            Subjects this week
          </p>
          {[
            { s: "Physics", w: "w-4/5" },
            { s: "Mathematics", w: "w-3/5" },
            { s: "Chemistry", w: "w-2/5" },
          ].map((row) => (
            <div key={row.s} className="mt-2.5">
              <p className="text-xs font-bold text-[#17201A]">{row.s}</p>
              <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-[#E2E8F0]">
                <div className={`h-full rounded-full bg-[#16A34A] ${row.w}`} />
              </div>
            </div>
          ))}
        </div>
        <div className="rounded-2xl border border-[#FACC15]/40 bg-[#FEF9C3] p-4">
          <p className="flex items-center gap-1.5 text-[11px] font-extrabold uppercase tracking-[0.12em] text-[#14532D]">
            <Target className="h-3.5 w-3.5" />
            Priority area
          </p>
          <p className="mt-1.5 text-sm font-bold text-[#14532D]">
            Physics: Wave Optics — from your last 2 practice sessions
          </p>
        </div>
      </div>
    </div>
    <p className="mt-4 flex items-center gap-2 text-xs font-semibold text-[#647067]">
      <CalendarCheck className="h-3.5 w-3.5 text-[#16A34A]" />
      The plan adapts to what practice reveals — weak spots move up
      automatically.
    </p>
  </div>
);

export const StudyPlannerSection = () => (
  <Showcase
    sectionTestId="study-planner-showcase"
    eyebrowTestId="study-planner-eyebrow"
    headlineTestId="study-planner-headline"
    ctaTestId="study-planner-cta"
    eyebrow="Study Planner"
    headline="Know what to study next."
    copy="Plan your preparation around your exams, available time, subjects, and the areas that need more attention."
    ctaHref="/signup"
  >
    <StudyPlannerVisual />
  </Showcase>
);

/* ---------- 5. Personalized Progress (text LEFT / visual RIGHT) ---------- */

const ProgressVisual = () => (
  <div>
    <div className="grid grid-cols-3 gap-3">
      {[
        { label: "Practiced", value: "142", sub: "questions" },
        { label: "Correct", value: "119", sub: "answers" },
        { label: "To revisit", value: "23", sub: "answers" },
      ].map((s) => (
        <div
          key={s.label}
          className="rounded-2xl border border-[#E2E8F0] bg-[#F8FAF7] p-3.5 text-center sm:p-4"
        >
          <p className="font-display text-xl font-extrabold text-[#17201A] sm:text-2xl">
            {s.value}
          </p>
          <p className="mt-0.5 text-[10px] font-bold uppercase tracking-wide text-[#647067]">
            {s.label}
          </p>
          <p className="text-[10px] font-medium text-[#94A39B]">{s.sub}</p>
        </div>
      ))}
    </div>
    <div className="mt-4 rounded-2xl border border-[#E2E8F0] bg-white p-4 sm:p-5">
      <p className="text-[11px] font-extrabold uppercase tracking-[0.12em] text-[#647067]">
        Subject performance
      </p>
      {[
        { s: "Mathematics", w: "w-4/5", strong: true },
        { s: "Physics", w: "w-3/5", strong: false },
        { s: "Chemistry", w: "w-1/2", strong: false },
      ].map((row) => (
        <div key={row.s} className="mt-3">
          <div className="flex items-center justify-between">
            <p className="text-xs font-bold text-[#17201A]">{row.s}</p>
            {row.strong && (
              <span className="inline-flex items-center gap-1 text-[10px] font-bold text-[#16A34A]">
                <TrendingUp className="h-3 w-3" />
                Improving
              </span>
            )}
          </div>
          <div className="mt-1 h-2 overflow-hidden rounded-full bg-[#E2E8F0]">
            <div className={`h-full rounded-full bg-[#16A34A] ${row.w}`} />
          </div>
        </div>
      ))}
    </div>
    <div className="mt-4 grid gap-4 sm:grid-cols-2">
      <div className="rounded-2xl border border-[#FACC15]/40 bg-[#FEF9C3] p-4">
        <p className="flex items-center gap-1.5 text-[11px] font-extrabold uppercase tracking-[0.12em] text-[#14532D]">
          <Target className="h-3.5 w-3.5" />
          Weak areas
        </p>
        <div className="mt-2 flex flex-wrap gap-1.5">
          {["Integration by Parts", "Wave Optics", "Aldehydes"].map((w) => (
            <span
              key={w}
              className="rounded-full bg-white px-2.5 py-1 text-[11px] font-bold text-[#14532D]"
            >
              {w}
            </span>
          ))}
        </div>
      </div>
      <div className="rounded-2xl border border-[#E2E8F0] bg-[#F8FAF7] p-4">
        <p className="text-[11px] font-extrabold uppercase tracking-[0.12em] text-[#647067]">
          Recent activity
        </p>
        <ul className="mt-2 space-y-1.5 text-xs font-semibold text-[#647067]">
          <li className="flex items-center gap-2">
            <RotateCcw className="h-3 w-3 shrink-0 text-[#16A34A]" />
            Reattempted Integration set
          </li>
          <li className="flex items-center gap-2">
            <PenLine className="h-3 w-3 shrink-0 text-[#16A34A]" />
            Physics practice — 5 questions
          </li>
          <li className="flex items-center gap-2">
            <Flame className="h-3 w-3 shrink-0 text-[#16A34A]" />
            7-day practice streak
          </li>
        </ul>
      </div>
    </div>
  </div>
);

export const ProgressSection = () => (
  <Showcase
    flip
    sectionTestId="progress-showcase"
    eyebrowTestId="progress-eyebrow"
    headlineTestId="progress-headline"
    ctaTestId="progress-cta"
    eyebrow="Personalized Progress"
    headline="See where your preparation is actually improving."
    copy="Understand your performance, identify weak areas, and use those insights to make your next practice session more useful."
    ctaHref="/progress"
  >
    <ProgressVisual />
  </Showcase>
);
